import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  adminGetSettings, adminUpdateSettings, adminCpaLog, adminCpaTest, adminCpaClicks,
} from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import {
  DollarSign, Webhook, Copy, Check, ExternalLink, AlertTriangle, RefreshCcw,
  PlayCircle, Send, MessageCircle, ShieldCheck, Shield, Globe, Zap, GitMerge,
  Network, Smartphone, Clock,
} from 'lucide-react';

const PUBLIC_BASE = (() => {
  try {
    const url = process.env.REACT_APP_BACKEND_URL || '';
    return url.replace(/\/+$/, '');
  } catch {
    return '';
  }
})();

const EVENT_LABELS = {
  tg_join: 'Telegram Join',
  registration: 'Broker Registration',
  ftd: 'Broker FTD (First Deposit)',
};

const EVENT_ICONS = {
  tg_join: MessageCircle,
  registration: ShieldCheck,
  ftd: DollarSign,
};

function CopyBtn({ text, label }) {
  const [done, setDone] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      toast.success(`${label || 'Copied'} copied`);
      setTimeout(() => setDone(false), 1500);
    } catch { toast.error('Copy failed'); }
  }
  return (
    <Button
      type="button" size="sm" variant="outline" onClick={copy}
      className="h-8 px-2 border-white/10 bg-white/[0.02] hover:bg-white/[0.06]"
      data-testid={`cpa-copy-${(label || 'value').toLowerCase().replace(/\s+/g, '-')}`}
    >
      {done ? <Check className="h-3.5 w-3.5 text-[hsl(152_78%_55%)]" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );
}

function FraudToggle({ icon: Icon, testid, label, hint, checked, onChange }) {
  return (
    <div className="flex items-start gap-3 rounded-md border border-white/10 bg-white/[0.02] p-3">
      <Icon className="h-4 w-4 text-[hsl(43_92%_56%)] mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="text-[12px] font-medium text-[hsl(0_0%_92%)]">{label}</div>
          <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
        </div>
        <div className="text-[10.5px] text-muted-foreground leading-snug">{hint}</div>
      </div>
    </div>
  );
}

function NumberField({ label, value, onChange, hint, icon: Icon }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </Label>
      <div className="mt-1 relative">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
        <Input
          type="number" min="0" step="0.01" inputMode="decimal"
          value={value ?? 0}
          onChange={(e) => onChange(parseFloat(e.target.value || '0'))}
          className="pl-6 bg-white/[0.02] border-white/10 text-sm font-mono"
          data-testid={`cpa-payout-${label.toLowerCase().replace(/\s+/g, '-')}`}
        />
      </div>
      {hint && <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

export default function AdminCpaTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [log, setLog] = useState({ items: [], by_event: {} });
  const [logLoading, setLogLoading] = useState(false);
  const [clicks, setClicks] = useState({ items: [] });
  const [clicksLoading, setClicksLoading] = useState(false);
  const [testing, setTesting] = useState(false);

  const loadSettings = useCallback(async () => {
    try { setS(await adminGetSettings()); }
    catch { toast.error('Failed to load settings'); }
  }, []);

  const loadLog = useCallback(async () => {
    setLogLoading(true);
    try { setLog(await adminCpaLog(80)); }
    catch { toast.error('Failed to load log'); }
    finally { setLogLoading(false); }
  }, []);

  const loadClicks = useCallback(async () => {
    setClicksLoading(true);
    try { setClicks(await adminCpaClicks(80)); }
    catch { toast.error('Failed to load clicks'); }
    finally { setClicksLoading(false); }
  }, []);

  useEffect(() => { loadSettings(); loadLog(); loadClicks(); }, [loadSettings, loadLog, loadClicks]);

  function up(k, v) { setS((prev) => ({ ...prev, [k]: v })); }

  async function save() {
    setSaving(true);
    try {
      const payload = {
        ...s,
        adsterra_payout_tg_join: parseFloat(s.adsterra_payout_tg_join || 0),
        adsterra_payout_registration: parseFloat(s.adsterra_payout_registration || 0),
        adsterra_payout_ftd: parseFloat(s.adsterra_payout_ftd || 0),
        cpa_fraud_min_seconds_to_convert: parseInt(s.cpa_fraud_min_seconds_to_convert || 0, 10),
        cpa_fraud_repeat_window_hours: parseInt(s.cpa_fraud_repeat_window_hours || 0, 10),
        joined_base: Number(s.joined_base) || 131312,
      };
      await adminUpdateSettings(payload);
      toast.success('CPA settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  }

  async function runTest(eventKey) {
    setTesting(true);
    try {
      const r = await adminCpaTest(eventKey, `test_${Date.now()}`);
      const result = r?.result || {};
      if (result.skipped_reason) {
        toast.warning(`Skipped: ${result.skipped_reason.replace(/_/g, ' ')}`);
      } else if (result.status_code >= 200 && result.status_code < 400) {
        toast.success(`Test ${EVENT_LABELS[eventKey]} fired (HTTP ${result.status_code})`);
      } else {
        toast.error(`Postback failed (HTTP ${result.status_code})`);
      }
      await loadLog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed');
    } finally { setTesting(false); }
  }

  if (!s) return <div className="text-sm text-muted-foreground">Loading CPA settings…</div>;

  const trackingParam = s.adsterra_tracking_param || 'aff_sub';
  // Adsterra REQUIRES this exact macro format with literal "(action)" — they reject
  // (registration) / (ftd) variants. The actual event binding happens in their
  // Multi-event tracking section, NOT in the URL macro.
  const landingUrl = `https://alveetrade.com/?${trackingParam}=##SUB_ID_SHORT(action)##`;

  return (
    <div className="space-y-5" data-testid="admin-cpa-tab">
      {/* Header / status */}
      <Card className="card-surface">
        <CardHeader className="flex flex-row items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Webhook className="h-4 w-4 text-[hsl(43_92%_56%)]" />
              Adsterra · CPA S2S Postback
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl leading-relaxed">
              Track conversions back to your Adsterra campaigns. We capture <code className="font-mono text-[10px]">?{trackingParam}=&lt;subid&gt;</code>
              {' '}from incoming visitors, persist it for 30 days, and fire your Adsterra postback URL whenever
              a Telegram join, broker registration, or FTD happens — with the configured fixed payout.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Enabled</Label>
            <Switch
              checked={!!s.adsterra_enabled}
              onCheckedChange={(v) => up('adsterra_enabled', v)}
              data-testid="cpa-enabled-toggle"
            />
          </div>
        </CardHeader>
      </Card>

      {/* Setup form */}
      <Card className="card-surface">
        <CardHeader>
          <CardTitle className="text-sm">Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Postback URL */}
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Adsterra S2S Postback URL (template)
            </Label>
            <Input
              value={s.adsterra_postback_url || ''}
              onChange={(e) => up('adsterra_postback_url', e.target.value)}
              placeholder="https://atrck.adsterra.com/postback?cnv_id={subid}&payout={payout}"
              className="mt-1 bg-white/[0.02] border-white/10 text-sm font-mono"
              data-testid="cpa-postback-url"
            />
            <div className="text-[10px] text-muted-foreground mt-1 leading-relaxed">
              Paste the URL Adsterra gave you for S2S postbacks. Use placeholders:
              <code className="font-mono ml-1">{'{subid}'}</code> /
              <code className="font-mono ml-1">{'{cnv_id}'}</code> /
              <code className="font-mono ml-1">{'{clickid}'}</code> for the visitor's
              click id, and <code className="font-mono ml-1">{'{payout}'}</code> for the configured payout.
              For Adsterra <strong>multi-event / variable payouts</strong> setups, you can also append
              <code className="font-mono mx-1 text-[hsl(43_92%_56%)]">&action={'{event}'}</code> so Adsterra
              gets told which event fired (we substitute <code className="font-mono">{'{event}'}</code> with
              <code className="font-mono"> tg_join</code> / <code className="font-mono">registration</code> /
              <code className="font-mono">ftd</code>).
            </div>
          </div>

          {/* Tracking param */}
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                Tracking parameter (URL key)
              </Label>
              <Input
                value={s.adsterra_tracking_param || 'aff_sub'}
                onChange={(e) => up('adsterra_tracking_param', e.target.value.trim() || 'aff_sub')}
                className="mt-1 bg-white/[0.02] border-white/10 text-sm font-mono"
                placeholder="aff_sub"
                data-testid="cpa-tracking-param"
              />
              <div className="text-[10px] text-muted-foreground mt-1">
                Default <code className="font-mono">aff_sub</code> — change only if your Adsterra campaign uses a different URL key.
              </div>
            </div>
            <div className="flex items-end">
              <div className="text-[10px] text-muted-foreground leading-relaxed">
                Other URL keys (<code className="font-mono">subid</code>, <code className="font-mono">cnv_id</code>,
                <code className="font-mono"> click_id</code>, <code className="font-mono">clickid</code>) are
                also auto-detected on landing as fallbacks.
              </div>
            </div>
          </div>

          <Separator className="bg-white/5" />

          {/* Payouts */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3 flex items-center gap-1.5">
              <DollarSign className="h-3 w-3" /> Fixed payouts per conversion
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              <NumberField
                label="Telegram Join"
                value={s.adsterra_payout_tg_join}
                onChange={(v) => up('adsterra_payout_tg_join', v)}
                icon={MessageCircle}
                hint="Fired when visitor clicks any Join Telegram CTA"
              />
              <NumberField
                label="Broker Registration"
                value={s.adsterra_payout_registration}
                onChange={(v) => up('adsterra_payout_registration', v)}
                icon={ShieldCheck}
                hint="Fired on PO postback event=registration"
              />
              <NumberField
                label="Broker FTD"
                value={s.adsterra_payout_ftd}
                onChange={(v) => up('adsterra_payout_ftd', v)}
                icon={DollarSign}
                hint="Fired on PO postback event=ftd"
              />
            </div>
          </div>

          <div className="flex items-center justify-end pt-1">
            <Button
              onClick={save} disabled={saving}
              className="bg-[hsl(43_92%_56%)] hover:bg-[hsl(43_92%_50%)] text-black font-semibold gap-2"
              data-testid="cpa-save-btn"
            >
              {saving ? 'Saving…' : 'Save CPA settings'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Fraud protection */}
      <Card className="card-surface">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Shield className="h-4 w-4 text-[hsl(43_92%_56%)]" /> Fraud protection
            <Badge variant="outline" className="border-white/10 text-[10px] font-normal">
              CPA-only — never blocks real visitors
            </Badge>
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl leading-relaxed">
            These rules apply <strong>only when firing the Adsterra postback</strong>. Real visitors can
            still browse the site and click Telegram / register on Pocket Option — we just refuse to
            credit Adsterra for traffic that looks fake. Every blocked attempt is recorded in the log
            below with the exact reason so you can audit.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <FraudToggle
              icon={Shield}
              testid="fraud-toggle-vpn"
              label="Block VPN / Proxy / TOR"
              hint="If the landing IP is flagged as a public proxy, VPN exit node, or TOR relay — skip the postback."
              checked={!!s.cpa_fraud_block_proxy_vpn}
              onChange={(v) => up('cpa_fraud_block_proxy_vpn', v)}
            />
            <FraudToggle
              icon={Globe}
              testid="fraud-toggle-hosting"
              label="Block datacenter / hosting IPs"
              hint="Blocks traffic from cloud servers (AWS, Google Cloud, OVH…) that are typically bots, not real users."
              checked={!!s.cpa_fraud_block_hosting}
              onChange={(v) => up('cpa_fraud_block_hosting', v)}
            />
            <FraudToggle
              icon={Globe}
              testid="fraud-toggle-cc-mismatch"
              label="Block country mismatch"
              hint="Skip when the landing country differs from the conversion country (e.g. landed from BD but Telegram clicked from US)."
              checked={!!s.cpa_fraud_block_country_mismatch}
              onChange={(v) => up('cpa_fraud_block_country_mismatch', v)}
            />
            <FraudToggle
              icon={GitMerge}
              testid="fraud-toggle-dedupe"
              label="Dedupe per (subid + event)"
              hint="Pay only for the FIRST conversion of each event per Adsterra click — repeat tg_join / registration / ftd attempts are skipped."
              checked={!!s.cpa_fraud_dedupe_per_event}
              onChange={(v) => up('cpa_fraud_dedupe_per_event', v)}
            />
            <FraudToggle
              icon={Network}
              testid="fraud-toggle-ip-repeat"
              label="Block same-IP repeat conversions"
              hint="Same IP address can't be paid for the same event again within the rolling window — even if they click a different Adsterra ad."
              checked={!!s.cpa_fraud_block_ip_repeat}
              onChange={(v) => up('cpa_fraud_block_ip_repeat', v)}
            />
            <FraudToggle
              icon={Smartphone}
              testid="fraud-toggle-device-repeat"
              label="Block same-device repeat conversions"
              hint="Same browser / phone fingerprint (UA + screen + timezone + platform) can't double-claim the same event in the rolling window."
              checked={!!s.cpa_fraud_block_device_repeat}
              onChange={(v) => up('cpa_fraud_block_device_repeat', v)}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-1">
                <Zap className="h-3 w-3" /> Minimum seconds before conversion counts
              </Label>
              <div className="flex items-center gap-3 mt-1.5">
                <Input
                  type="number" min="0" max="3600" step="1" inputMode="numeric"
                  value={s.cpa_fraud_min_seconds_to_convert ?? 30}
                  onChange={(e) => up('cpa_fraud_min_seconds_to_convert', parseInt(e.target.value || '0', 10))}
                  className="w-28 bg-white/[0.02] border-white/10 text-sm font-mono"
                  data-testid="fraud-min-seconds"
                />
                <span className="text-xs text-muted-foreground">
                  Faster than this = bot. Set <code className="font-mono">0</code> to disable.
                </span>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" /> Same-IP / Same-device repeat window (hours)
              </Label>
              <div className="flex items-center gap-3 mt-1.5">
                <Input
                  type="number" min="0" max="720" step="1" inputMode="numeric"
                  value={s.cpa_fraud_repeat_window_hours ?? 24}
                  onChange={(e) => up('cpa_fraud_repeat_window_hours', parseInt(e.target.value || '0', 10))}
                  className="w-28 bg-white/[0.02] border-white/10 text-sm font-mono"
                  data-testid="fraud-repeat-window"
                />
                <span className="text-xs text-muted-foreground">
                  Within this window, same IP / device can be paid only once per event. Default <code className="font-mono">24</code>h.
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-md border border-[rgba(245,185,66,0.2)] bg-[rgba(245,185,66,0.04)] p-2.5 text-[11px] text-muted-foreground leading-relaxed">
            <strong className="text-[hsl(43_92%_56%)]">How it works:</strong> When a visitor lands with{' '}
            <code className="font-mono">?aff_sub=…</code> we register the click + IP intel (proxy / hosting /
            country / ASN) on the backend. Later when a conversion happens we look up that record, apply
            the rules above, and only fire the Adsterra postback if it passes. Blocked attempts show as
            <Badge className="mx-1 bg-[rgba(245,185,66,0.12)] text-[hsl(43_92%_56%)] border border-[rgba(245,185,66,0.3)] text-[10px]">
              skipped · fraud_*
            </Badge>
            in the postback log.
          </div>

          <div className="flex items-center justify-end pt-1">
            <Button
              onClick={save} disabled={saving}
              variant="outline"
              className="border-[rgba(245,185,66,0.4)] bg-[rgba(245,185,66,0.06)] text-[hsl(43_92%_56%)] hover:bg-[rgba(245,185,66,0.12)] hover:text-[hsl(43_92%_56%)] gap-2"
              data-testid="cpa-fraud-save-btn"
            >
              <Shield className="h-4 w-4" /> {saving ? 'Saving…' : 'Save fraud settings'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Setup helper */}
      <Card className="card-surface">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <PlayCircle className="h-4 w-4 text-[hsl(43_92%_56%)]" /> How to set up in Adsterra
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border border-[rgba(245,185,66,0.25)] bg-[rgba(245,185,66,0.04)] p-3 text-xs leading-relaxed">
            <div className="font-semibold text-[hsl(43_92%_56%)] mb-2">Steps</div>
            <ol className="list-decimal pl-4 space-y-1.5 text-[hsl(0_0%_85%)]">
              <li>In Adsterra, go to <strong>Advertiser → Campaigns → Create / Edit</strong>.</li>
              <li>
                Set <strong>Target / Tracker URL</strong> using the Adsterra-specific format below — it
                MUST contain <code className="font-mono mx-1">##SUB_ID_SHORT(action)##</code>, otherwise
                Adsterra's URL validator will reject it.
              </li>
              <li>
                Open the campaign's <strong>Conversion Tracking / Postback</strong> section, copy the postback URL
                (looks like <code className="font-mono text-[10px]">https://www.pbterra.com/name/.../at?subid_short={'{clickid}'}&atpay={'{payout}'}</code>) and paste it
                into the field above.
              </li>
              <li>Set per-event payouts above to match what you've negotiated in Adsterra.</li>
              <li>Toggle <strong>Enabled</strong> on, click <strong>Save CPA settings</strong>.</li>
              <li>
                Back in Adsterra, paste your tracker URL into the <strong>"Check the URL"</strong> field — it should
                pass validation now.
              </li>
              <li>Click <strong>"Make a test conversion"</strong> — Adsterra simulates a click; you'll see it appear in the log below.</li>
            </ol>
          </div>

          <div className="rounded-md border border-[rgba(225,29,46,0.35)] bg-[rgba(225,29,46,0.06)] p-3 flex gap-2 items-start">
            <AlertTriangle className="h-4 w-4 text-[hsl(0_72%_55%)] mt-0.5 flex-shrink-0" />
            <div className="text-[11px] leading-relaxed text-[hsl(0_0%_88%)] space-y-1">
              <div><strong>Common error: "URL must contain ##SUB_ID_SHORT(action)##"</strong></div>
              <div className="text-muted-foreground">
                Adsterra accepts <strong>only one literal format</strong>:
                <code className="font-mono mx-1 text-[hsl(43_92%_56%)]">##SUB_ID_SHORT(action)##</code>.
                The word <code className="font-mono mx-1">action</code> inside the parentheses must be
                <strong> kept literally</strong> — don't replace it with <code className="font-mono">registration</code>,
                <code className="font-mono"> ftd</code>, or any other event name. Adsterra binds the actual
                event(s) to your campaign through the <strong>"Multi-event tracking"</strong> section in their
                campaign settings, NOT via the URL macro. One single tracker URL works for all events.
              </div>
            </div>
          </div>

          {/* iOS Safari paste warning — the actual cause of most "URL contains an unsupported placeholder" errors */}
          <div className="rounded-md border border-[rgba(245,185,66,0.5)] bg-[rgba(245,185,66,0.08)] p-3 flex gap-2 items-start">
            <AlertTriangle className="h-4 w-4 text-[hsl(43_92%_56%)] mt-0.5 flex-shrink-0" />
            <div className="text-[11px] leading-relaxed text-[hsl(0_0%_92%)] space-y-1.5">
              <div className="font-semibold text-[hsl(43_92%_56%)]">
                ⚠️ iOS Safari / mobile browsers auto-encode <code className="font-mono">#</code> as <code className="font-mono">%23</code> on paste
              </div>
              <div className="text-[hsl(0_0%_82%)]">
                If Adsterra rejects with <em>"URL contains an unsupported placeholder"</em>, the
                <code className="font-mono">##</code> got converted to <code className="font-mono">%23%23</code> on paste. Fixes:
              </div>
              <ol className="list-decimal pl-4 space-y-1 text-[hsl(0_0%_82%)]">
                <li>After pasting, tap inside the field and manually replace <code className="font-mono">%23</code> with <code className="font-mono">#</code>.</li>
                <li>Open this admin tab on a <strong>desktop browser</strong> and copy from there — desktop clipboards don't auto-encode URLs.</li>
                <li>Paste into iOS Notes app first, then copy from Notes (preserves raw <code className="font-mono">#</code>) and paste into Adsterra.</li>
              </ol>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">
              Adsterra Tracker URL · the only format that works
            </div>
            <div className="flex items-center gap-2 rounded-md border border-[rgba(245,185,66,0.45)] bg-[rgba(245,185,66,0.06)] px-3 py-2.5">
              <code className="font-mono text-[12px] flex-1 break-all text-[hsl(43_92%_56%)] font-semibold" data-testid="cpa-landing-url">
                {landingUrl}
              </code>
              <CopyBtn text={landingUrl} label="Tracker URL" />
            </div>
            <div className="text-[10px] text-muted-foreground mt-1.5 leading-relaxed">
              Paste this exactly into Adsterra's <strong>"Check the URL"</strong> field. The text
              <code className="font-mono mx-1">(action)</code> must remain literal — Adsterra rejects
              <code className="font-mono mx-1">(registration)</code> / <code className="font-mono">(ftd)</code>.
              Configure which events count toward this URL inside Adsterra's
              <strong> Multi-event tracking</strong> section.
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">
              Backend endpoints (informational)
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <div className="rounded-md border border-border bg-white/[0.02] px-3 py-2 text-[11px] font-mono break-all">
                {PUBLIC_BASE}/api/track <span className="text-muted-foreground">· tg_join</span>
              </div>
              <div className="rounded-md border border-border bg-white/[0.02] px-3 py-2 text-[11px] font-mono break-all">
                {PUBLIC_BASE}/api/postback/po <span className="text-muted-foreground">· registration / ftd</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Test buttons */}
      <Card className="card-surface">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Send className="h-4 w-4 text-[hsl(43_92%_56%)]" /> Test postback
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Sends a synthetic postback to Adsterra using the configured URL + payout for that event.
            Useful to verify wiring without actually completing a real conversion.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-3">
            {Object.entries(EVENT_LABELS).map(([k, label]) => {
              const Icon = EVENT_ICONS[k];
              return (
                <Button
                  key={k}
                  variant="outline"
                  disabled={testing || !s.adsterra_enabled || !s.adsterra_postback_url}
                  onClick={() => runTest(k)}
                  className="h-12 gap-2 border-[rgba(245,185,66,0.25)] bg-white/[0.02] hover:bg-[rgba(245,185,66,0.06)] hover:text-[hsl(43_92%_56%)]"
                  data-testid={`cpa-test-${k}`}
                >
                  <Icon className="h-4 w-4" /> Test {label}
                </Button>
              );
            })}
          </div>
          {(!s.adsterra_enabled || !s.adsterra_postback_url) && (
            <div className="mt-3 rounded-md border border-[rgba(225,29,46,0.35)] bg-[rgba(225,29,46,0.06)] p-2 text-[11px] flex gap-2 items-start">
              <AlertTriangle className="h-3.5 w-3.5 text-[hsl(0_72%_55%)] mt-0.5 flex-shrink-0" />
              <span>Enable Adsterra above and paste a postback URL before running tests.</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* CPA clicks (landings) */}
      <Card className="card-surface">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Globe className="h-4 w-4 text-[hsl(43_92%_56%)]" /> Recent CPA landings
            <span className="text-xs text-muted-foreground font-normal">· {clicks.items.length}</span>
          </CardTitle>
          <Button size="sm" variant="outline" onClick={loadClicks} disabled={clicksLoading}
            className="h-9 border-white/10 bg-white/[0.02] gap-1">
            <RefreshCcw className={`h-3.5 w-3.5 ${clicksLoading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </CardHeader>
        <CardContent>
          <p className="text-[11px] text-muted-foreground mb-3">
            Visitors who landed with an <code className="font-mono">?aff_sub=…</code> param. Each row shows
            the IP intel captured at landing time — used by the fraud rules above when a conversion happens.
          </p>
          {clicks.items.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-6">
              No CPA landings recorded yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-muted-foreground border-b border-white/5">
                    <th className="py-2 pr-2">Time</th>
                    <th className="py-2 pr-2">Subid</th>
                    <th className="py-2 pr-2">Country</th>
                    <th className="py-2 pr-2">IP</th>
                    <th className="py-2 pr-2">ISP / ASN</th>
                    <th className="py-2 pr-2">Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {clicks.items.map((row) => {
                    const dt = row.landed_at ? new Date(row.landed_at) : null;
                    return (
                      <tr key={row.subid + (row.landed_at || '')} className="border-b border-white/5">
                        <td className="py-2 pr-2 whitespace-nowrap text-muted-foreground">
                          {dt ? dt.toLocaleString() : '—'}
                        </td>
                        <td className="py-2 pr-2 font-mono text-[11px] truncate max-w-[140px]">
                          {row.subid}
                        </td>
                        <td className="py-2 pr-2 whitespace-nowrap">
                          {row.country_code || '—'}
                        </td>
                        <td className="py-2 pr-2 font-mono text-[11px] whitespace-nowrap">
                          {row.ip || '—'}
                        </td>
                        <td className="py-2 pr-2 text-[11px] truncate max-w-[180px] text-muted-foreground">
                          {row.isp || row.asn || '—'}
                        </td>
                        <td className="py-2 pr-2">
                          <div className="flex flex-wrap gap-1">
                            {row.is_proxy && (
                              <Badge className="bg-[rgba(225,29,46,0.12)] text-[hsl(0_72%_60%)] border border-[rgba(225,29,46,0.3)] text-[10px]">VPN/Proxy</Badge>
                            )}
                            {row.is_hosting && (
                              <Badge className="bg-[rgba(225,29,46,0.12)] text-[hsl(0_72%_60%)] border border-[rgba(225,29,46,0.3)] text-[10px]">Datacenter</Badge>
                            )}
                            {row.is_mobile && (
                              <Badge className="bg-[rgba(34,199,132,0.10)] text-[hsl(152_78%_55%)] border border-[rgba(34,199,132,0.25)] text-[10px]">Mobile</Badge>
                            )}
                            {!row.is_proxy && !row.is_hosting && (
                              <Badge variant="outline" className="border-white/10 text-[10px] font-normal text-muted-foreground">clean</Badge>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Log */}
      <Card className="card-surface">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            Recent postback attempts
            <span className="text-xs text-muted-foreground font-normal">· {log.items.length}</span>
            {Object.entries(log.by_event || {}).map(([k, n]) => (
              <Badge key={k} variant="outline" className="border-white/10 text-[10px] font-normal">
                {k}: {n}
              </Badge>
            ))}
          </CardTitle>
          <Button size="sm" variant="outline" onClick={loadLog} disabled={logLoading}
            className="h-9 border-white/10 bg-white/[0.02] gap-1">
            <RefreshCcw className={`h-3.5 w-3.5 ${logLoading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {log.items.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-8">
              No postbacks fired yet. They'll appear here once visitors arrive with an
              <code className="font-mono mx-1">?{trackingParam}=…</code> param and complete a tracked event.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-muted-foreground border-b border-white/5">
                    <th className="py-2 pr-2">Time</th>
                    <th className="py-2 pr-2">Event</th>
                    <th className="py-2 pr-2">Subid</th>
                    <th className="py-2 pr-2">Country</th>
                    <th className="py-2 pr-2">Payout</th>
                    <th className="py-2 pr-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {log.items.map((row) => {
                    const dt = row.created_at ? new Date(row.created_at) : null;
                    const ok = row.status_code && row.status_code >= 200 && row.status_code < 400;
                    const skipped = !!row.skipped_reason;
                    return (
                      <tr key={row.id} className="border-b border-white/5">
                        <td className="py-2 pr-2 whitespace-nowrap text-muted-foreground">
                          {dt ? dt.toLocaleString() : '—'}
                        </td>
                        <td className="py-2 pr-2">
                          <Badge variant="outline" className="border-white/10 text-[10px] font-normal">
                            {row.event_key}
                          </Badge>
                        </td>
                        <td className="py-2 pr-2 font-mono text-[11px] truncate max-w-[140px]">
                          {row.subid || <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="py-2 pr-2">{row.country_code || '—'}</td>
                        <td className="py-2 pr-2 font-mono">${(row.payout || 0).toFixed(2)}</td>
                        <td className="py-2 pr-2">
                          {skipped ? (
                            <Badge className="bg-[rgba(245,185,66,0.12)] text-[hsl(43_92%_56%)] border border-[rgba(245,185,66,0.3)] text-[10px]">
                              skipped · {row.skipped_reason.replace(/_/g, ' ')}
                            </Badge>
                          ) : ok ? (
                            <Badge className="bg-[rgba(34,199,132,0.12)] text-[hsl(152_78%_55%)] border border-[rgba(34,199,132,0.32)] text-[10px]">
                              {row.status_code} OK
                            </Badge>
                          ) : (
                            <Badge className="bg-[rgba(225,29,46,0.12)] text-[hsl(0_72%_60%)] border border-[rgba(225,29,46,0.3)] text-[10px]">
                              {row.status_code || 'err'}
                            </Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <a
        href="https://partners.adsterra.com/" target="_blank" rel="noreferrer"
        className="inline-flex items-center gap-1.5 text-xs text-[hsl(43_92%_56%)] hover:underline"
        data-testid="open-adsterra-dashboard"
      >
        Open Adsterra Partners <ExternalLink className="h-3 w-3" />
      </a>
    </div>
  );
}
